import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import { 
  exportToCSV, 
  exportToJSON, 
  exportToExcel, 
  exportToPDF,
  formatForExport,
  getExportFilename 
} from '../../utils/exportUtils';
import {
  getBroadcasts,
  getBroadcast,
  getBroadcastSmsLogs,
  getBroadcastStats,
  createBroadcast,
  updateBroadcast,
  deleteBroadcast,
  cancelBroadcast,
  rescheduleBroadcast,
  clearApiMessage,
  clearError,
  clearBroadcastState,
  selectBroadcasts,
  selectBroadcastPagination,
  selectCurrentBroadcast,
  selectBroadcastSmsLogs,
  selectBroadcastStats,
  selectBroadcastsLoading,
  selectBroadcastsApiMessage,
  selectBroadcastsError,
  selectBroadcastStatsSummary,
} from '../../store/slices/main/broadcastSlice';
import {
  ClipboardList, Pencil, XCircle, Send, CheckCircle2, AlertTriangle, Trash2,
  BarChart3, Download, TrendingUp, FileText, Calendar, FileEdit, Search,
  Loader2, Inbox, Eye
} from 'lucide-react';

const MessageLog = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();
  
  // ==================== STATE ====================
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedBroadcastId, setSelectedBroadcastId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Refs for resend modal
  const resendRecipientRef = useRef(null);
  const resendMessageRef = useRef(null);

  // ==================== SELECTORS ====================
  const broadcasts = useSelector(selectBroadcasts) || [];
  const pagination = useSelector(selectBroadcastPagination) || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  };
  const currentBroadcast = useSelector(selectCurrentBroadcast);
  const smsLogs = useSelector(selectBroadcastSmsLogs) || [];
  const stats = useSelector(selectBroadcastStats);
  const statsSummary = useSelector(selectBroadcastStatsSummary);
  const loading = useSelector(selectBroadcastsLoading);
  const apiMessage = useSelector(selectBroadcastsApiMessage);
  const error = useSelector(selectBroadcastsError);

  // Track last toast message to prevent duplicates
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // ==================== EFFECTS ====================

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        await dispatch(getBroadcasts({ 
          per_page: itemsPerPage, 
          page: currentPage,
          search: searchTerm
        }));
        await dispatch(getBroadcastStats());
      } catch (err) {
        console.error('Error loading data:', err);
      }
    };
    
    loadData();
    
    return () => {
      dispatch(clearBroadcastState());
    };
  }, [dispatch]);

  // Handle search and pagination
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {
        per_page: itemsPerPage,
        page: currentPage,
        search: searchTerm
      };
      if (filter !== 'all') params.status = filter;
      
      dispatch(getBroadcasts(params));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, filter, currentPage, itemsPerPage, dispatch]);

  // Display API messages
  useEffect(() => {
    if (!apiMessage || !showToast) return;
    
    const messageKey = `${apiMessage}_${error ? 'error' : 'success'}`;
    const now = Date.now();
    
    if (lastToastRef.current.message === messageKey && 
        (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }
    
    lastToastRef.current = { message: messageKey, timestamp: now };
    
    const toastType = error ? 'error' : 'success';
    showToast(apiMessage, toastType);
    
    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, error, dispatch, showToast]);

  // ==================== TOAST HELPER ====================
  const showToastMessage = useCallback((message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  }, [showToast]);

  // ==================== HELPER FUNCTIONS ====================

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeColors = (type) => {
    const colors = {
      broadcast: 'var(--navy)',
      birthday: 'var(--gold)',
      poll: 'var(--blue)',
      visitor: 'var(--green)',
      drip: 'var(--purple)',
      event: 'var(--amber)'
    };
    return colors[type] || 'var(--text-light)';
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      sent: { class: 'badge-green', label: 'Sent' },
      delivered: { class: 'badge-green', label: 'Delivered' },
      pending: { class: 'badge-amber', label: 'Pending' },
      scheduled: { class: 'badge-blue', label: 'Scheduled' },
      failed: { class: 'badge-red', label: 'Failed' },
      cancelled: { class: 'badge-red', label: 'Cancelled' },
      draft: { class: 'badge-navy', label: 'Draft' }
    };
    const s = statusMap[status] || statusMap.pending;
    return <span className={`badge ${s.class}`}>{s.label}</span>;
  };

  const getStatusStats = () => {
    const total = broadcasts.length;
    const sent = broadcasts.filter(b => b.status === 'sent' || b.status === 'delivered').length;
    const scheduled = broadcasts.filter(b => b.status === 'scheduled').length;
    const drafts = broadcasts.filter(b => b.status === 'draft').length;
    const failed = broadcasts.filter(b => b.status === 'failed').length;
    const cancelled = broadcasts.filter(b => b.status === 'cancelled').length;
    
    return {
      total,
      sent,
      scheduled,
      drafts,
      failed,
      cancelled
    };
  };

  // ==================== SORTING ====================

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  // ==================== SELECTION ====================

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedLogs(broadcasts.map(b => b.id));
    } else {
      setSelectedLogs([]);
    }
  };

  const handleSelectLog = (id) => {
    if (selectedLogs.includes(id)) {
      setSelectedLogs(selectedLogs.filter(logId => logId !== id));
    } else {
      setSelectedLogs([...selectedLogs, id]);
    }
  };

  // ==================== VIEW BROADCAST DETAIL ====================

  const handleViewBroadcast = async (broadcast) => {
    try {
      await dispatch(getBroadcast(broadcast.id));
      await dispatch(getBroadcastSmsLogs({ id: broadcast.id, params: { per_page: 20 } }));
      
      const logs = smsLogs || [];
      
      openModal(
        <div>
          <div className="modal-header">
            <div className="modal-title"><ClipboardList size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Broadcast Details</div>
            <button className="modal-close" onClick={closeModal}>×</button>
          </div>
          <div className="modal-body">
            <div style={{ display: 'grid', gap: '12px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
                <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Title</span>
                <span style={{ fontWeight: '600' }}>{broadcast.title}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
                <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Type</span>
                <span>
                  <span className="badge" style={{ 
                    background: `${getTypeColors(broadcast.type)}18`, 
                    color: getTypeColors(broadcast.type), 
                    fontSize: '12px' 
                  }}>
                    {broadcast.type || 'Broadcast'}
                  </span>
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
                <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Message</span>
                <span style={{ 
                  padding: '10px', 
                  background: 'var(--cream)', 
                  borderRadius: '8px',
                  lineHeight: '1.6'
                }}>
                  {broadcast.message}
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
                <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Recipients</span>
                <span>{broadcast.recipient_count || 0} members</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
                <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Created</span>
                <span>{formatDate(broadcast.created_at)}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
                <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Scheduled</span>
                <span>{broadcast.scheduled_at ? formatDate(broadcast.scheduled_at) : 'Immediate'}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
                <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Status</span>
                <span>{getStatusBadge(broadcast.status)}</span>
              </div>
              
              {logs.length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>
                    SMS Logs ({logs.length})
                  </div>
                  <div style={{ 
                    maxHeight: '200px', 
                    overflowY: 'auto',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    padding: '8px'
                  }}>
                    {logs.map((log, index) => (
                      <div key={log.id || index} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        padding: '6px 8px',
                        borderBottom: '1px solid var(--border-light)',
                        fontSize: '12px'
                      }}>
                        <span>{log.phone || log.recipient}</span>
                        <span className={`badge ${log.status === 'sent' || log.status === 'delivered' ? 'badge-green' : log.status === 'failed' ? 'badge-red' : 'badge-amber'}`}>
                          {log.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
            {broadcast.status === 'draft' && (
              <button className="btn btn-gold btn-sm" onClick={() => {
                closeModal();
                handleEditBroadcast(broadcast);
              }}><Pencil size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Edit</button>
            )}
            {broadcast.status === 'scheduled' && (
              <button className="btn btn-ghost btn-sm" onClick={() => {
                closeModal();
                handleCancelBroadcast(broadcast);
              }} style={{ color: 'var(--red)' }}><XCircle size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Cancel</button>
            )}
          </div>
        </div>
      );
    } catch (err) {
      showToastMessage(err.message || 'Failed to load broadcast details', 'error');
    }
  };

  // ==================== CREATE BROADCAST ====================

  const openCreateBroadcast = () => {
    setTimeout(() => {
      if (resendRecipientRef.current) resendRecipientRef.current.value = '';
      if (resendMessageRef.current) resendMessageRef.current.value = '';
    }, 0);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Send size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Create New Broadcast</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Title <span style={{ color: 'var(--red)' }}>*</span></label>
            <input className="form-input" placeholder="Broadcast title" ref={resendRecipientRef} />
          </div>
          <div className="form-group">
            <label className="form-label">Message <span style={{ color: 'var(--red)' }}>*</span></label>
            <textarea className="form-textarea" rows="4" placeholder="Enter your message..." ref={resendMessageRef} />
            <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '4px' }}>
              Use {'{FirstName}'} for personalisation
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Select Group</label>
            <select className="form-select">
              <option value="">All Members</option>
              <option value="1">Youth</option>
              <option value="2">Choir</option>
              <option value="3">Ushers</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Schedule (Optional)</label>
            <input type="datetime-local" className="form-input" />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const title = resendRecipientRef.current?.value?.trim() || '';
              const message = resendMessageRef.current?.value?.trim() || '';
              
              if (!title) {
                showToastMessage('Please enter a title', 'error');
                return;
              }
              if (!message) {
                showToastMessage('Please enter a message', 'error');
                return;
              }
              
              setIsSubmitting(true);
              try {
                await dispatch(createBroadcast({
                  title,
                  message,
                  type: 'broadcast',
                  status: 'draft'
                })).unwrap();
                
                closeModal();
                dispatch(getBroadcasts({ 
                  per_page: itemsPerPage, 
                  page: currentPage,
                  search: searchTerm
                }));
                dispatch(getBroadcastStats());
                showToastMessage('📨 Broadcast created successfully!', 'success');
              } catch (err) {
                showToastMessage(err.message || 'Failed to create broadcast', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Creating...</>
            ) : (
              'Create Broadcast'
            )}
          </button>
        </div>
      </div>
    );
  };

  // ==================== EDIT BROADCAST ====================

  const handleEditBroadcast = (broadcast) => {
    setTimeout(() => {
      if (resendRecipientRef.current) resendRecipientRef.current.value = broadcast.title || '';
      if (resendMessageRef.current) resendMessageRef.current.value = broadcast.message || '';
    }, 50);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Edit Broadcast</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Title <span style={{ color: 'var(--red)' }}>*</span></label>
            <input className="form-input" ref={resendRecipientRef} defaultValue={broadcast.title || ''} />
          </div>
          <div className="form-group">
            <label className="form-label">Message <span style={{ color: 'var(--red)' }}>*</span></label>
            <textarea className="form-textarea" rows="4" ref={resendMessageRef} defaultValue={broadcast.message || ''} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const title = resendRecipientRef.current?.value?.trim() || '';
              const message = resendMessageRef.current?.value?.trim() || '';
              
              if (!title) {
                showToastMessage('Please enter a title', 'error');
                return;
              }
              if (!message) {
                showToastMessage('Please enter a message', 'error');
                return;
              }
              
              setIsSubmitting(true);
              try {
                await dispatch(updateBroadcast({
                  id: broadcast.id,
                  data: { title, message }
                })).unwrap();
                
                closeModal();
                dispatch(getBroadcasts({ 
                  per_page: itemsPerPage, 
                  page: currentPage,
                  search: searchTerm
                }));
                showToastMessage('✅ Broadcast updated successfully!', 'success');
              } catch (err) {
                showToastMessage(err.message || 'Failed to update broadcast', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Saving...</>
            ) : (
              'Update Broadcast'
            )}
          </button>
        </div>
      </div>
    );
  };

  // ==================== CANCEL BROADCAST ====================

  const handleCancelBroadcast = (broadcast) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><XCircle size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Cancel Broadcast</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to cancel <strong>{broadcast.title}</strong>?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />This will cancel the scheduled broadcast.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                await dispatch(cancelBroadcast(broadcast.id)).unwrap();
                closeModal();
                dispatch(getBroadcasts({ 
                  per_page: itemsPerPage, 
                  page: currentPage,
                  search: searchTerm
                }));
                dispatch(getBroadcastStats());
                showToastMessage('❌ Broadcast cancelled successfully', 'error');
              } catch (err) {
                showToastMessage(err.message || 'Failed to cancel broadcast', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Cancelling...</>
            ) : (
              'Cancel Broadcast'
            )}
          </button>
        </div>
      </div>
    );
  };

  // ==================== DELETE BROADCAST ====================

  const handleDeleteBroadcast = (broadcast) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Delete Broadcast</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete <strong>{broadcast.title}</strong>?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />This action cannot be undone.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                await dispatch(deleteBroadcast(broadcast.id)).unwrap();
                closeModal();
                dispatch(getBroadcasts({ 
                  per_page: itemsPerPage, 
                  page: currentPage,
                  search: searchTerm
                }));
                dispatch(getBroadcastStats());
                showToastMessage(`🗑️ "${broadcast.title}" deleted`, 'error');
              } catch (err) {
                showToastMessage(err.message || 'Failed to delete broadcast', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Deleting...</>
            ) : (
              'Delete Broadcast'
            )}
          </button>
        </div>
      </div>
    );
  };

  // ==================== EXPORT FUNCTIONS ====================

  const handleExport = (format) => {
    if (broadcasts.length === 0) {
      showToastMessage('No broadcasts to export', 'error');
      return;
    }

    const fields = [
      { key: 'id', label: 'ID' },
      { key: 'title', label: 'Title' },
      { key: 'type', label: 'Type' },
      { key: 'message', label: 'Message' },
      { key: 'recipient_count', label: 'Recipients' },
      { key: 'status', label: 'Status' },
      { key: 'created_at', label: 'Created At' },
      { key: 'scheduled_at', label: 'Scheduled At' }
    ];

    const formattedData = broadcasts.map(b => ({
      id: b.id,
      title: b.title || 'N/A',
      type: b.type || 'broadcast',
      message: b.message || 'N/A',
      recipients: b.recipient_count || 0,
      status: b.status || 'pending',
      created_at: formatDate(b.created_at),
      scheduled_at: b.scheduled_at ? formatDate(b.scheduled_at) : 'Immediate'
    }));

    const filename = getExportFilename('broadcasts');

    switch (format) {
      case 'CSV':
        exportToCSV(formattedData, filename);
        break;
      case 'JSON':
        exportToJSON(formattedData, filename);
        break;
      case 'Excel':
        exportToExcel(formattedData, filename);
        break;
      case 'PDF':
        exportToPDF(formattedData, 'Broadcasts Report', filename);
        break;
      default:
        showToastMessage('Export format not supported', 'error');
        return;
    }
    
    showToastMessage(`📊 ${broadcasts.length} broadcasts exported as ${format}!`, 'success');
  };

  const openExportModal = () => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Download size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Export Broadcasts</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Export {broadcasts.length} broadcast entries in your preferred format.
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>
              Select Format
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {['CSV', 'JSON', 'Excel', 'PDF'].map(format => (
                <button
                  key={format}
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    handleExport(format);
                    closeModal();
                  }}
                  style={{ 
                    padding: '8px', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    height: 'auto',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--gold)';
                    e.currentTarget.style.background = 'var(--gold-pale)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <span style={{ display: 'flex' }}>
                    {format === 'CSV' && <BarChart3 size={20} />}
                    {format === 'JSON' && <ClipboardList size={20} />}
                    {format === 'Excel' && <TrendingUp size={20} />}
                    {format === 'PDF' && <FileText size={20} />}
                  </span>
                  <span style={{ fontSize: '11px' }}>{format}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{ 
            padding: '10px 12px', 
            background: 'var(--cream)', 
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-light)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span><BarChart3 size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />{broadcasts.length} records will be exported</span>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
        </div>
      </div>
    );
  };

  // ==================== BULK DELETE ====================

  const handleBulkDelete = () => {
    if (selectedLogs.length === 0) {
      showToastMessage('Please select at least one broadcast', 'error');
      return;
    }

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Bulk Delete</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete <strong>{selectedLogs.length}</strong> broadcast(s)?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />This action cannot be undone.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                for (const id of selectedLogs) {
                  await dispatch(deleteBroadcast(id)).unwrap();
                }
                setSelectedLogs([]);
                closeModal();
                dispatch(getBroadcasts({ 
                  per_page: itemsPerPage, 
                  page: currentPage,
                  search: searchTerm
                }));
                dispatch(getBroadcastStats());
                showToastMessage(`🗑️ ${selectedLogs.length} broadcasts deleted`, 'error');
              } catch (err) {
                showToastMessage(err.message || 'Failed to delete broadcasts', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Deleting...</>
            ) : (
              'Delete Selected'
            )}
          </button>
        </div>
      </div>
    );
  };

  // ==================== PAGINATION ====================

  const totalItems = pagination?.total || 0;
  const totalPages = pagination?.last_page || 1;
  const currentPageNum = pagination?.current_page || 1;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPageNum - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // ==================== RENDER ====================

  const statsData = statsSummary || stats || { 
    total: 0, 
    sent: 0, 
    scheduled: 0, 
    drafts: 0, 
    failed: 0,
    cancelled: 0
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
          Complete history of every broadcast sent through the platform.
          {selectedLogs.length > 0 && (
            <span style={{ marginLeft: '8px', fontWeight: '600', color: 'var(--navy)' }}>
              · {selectedLogs.length} selected
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button className="btn btn-gold btn-sm" onClick={openCreateBroadcast} disabled={loading || isSubmitting}>
            + New Broadcast
          </button>
          {selectedLogs.length > 0 && (
            <button className="btn btn-danger btn-sm" onClick={handleBulkDelete} disabled={loading || isSubmitting}>
              <Trash2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Delete Selected
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={openExportModal} disabled={loading}>
            <Download size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Export
          </button>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: '20px' }}>
        <div className="stat-card gold">
          <div className="stat-label">Total Broadcasts</div>
          <div className="stat-value">{statsData.total.toLocaleString()}</div>
          <div className="stat-icon"><Send size={20} /></div>
        </div>
        <div className="stat-card green">
          <div className="stat-label">Sent/Delivered</div>
          <div className="stat-value">{statsData.sent.toLocaleString()}</div>
          <div className="stat-change up"><CheckCircle2 size={13} /></div>
          <div className="stat-icon"><CheckCircle2 size={20} /></div>
        </div>
        <div className="stat-card blue">
          <div className="stat-label">Scheduled</div>
          <div className="stat-value">{statsData.scheduled.toLocaleString()}</div>
          <div className="stat-icon"><Calendar size={20} /></div>
        </div>
        <div className="stat-card amber">
          <div className="stat-label">Drafts</div>
          <div className="stat-value">{statsData.drafts.toLocaleString()}</div>
          <div className="stat-icon"><FileEdit size={20} /></div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Broadcast Log</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <select 
              className="form-select" 
              style={{ width: 'auto', padding: '6px 10px', fontSize: '12px' }} 
              value={filter} 
              onChange={(e) => setFilter(e.target.value)}
              disabled={loading}
            >
              <option value="all">All Status</option>
              <option value="sent">Sent</option>
              <option value="scheduled">Scheduled</option>
              <option value="draft">Draft</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <div className="search-box" style={{ maxWidth: '200px' }}>
              <span className="search-icon"><Search size={16} /></span>
              <input 
                type="text" 
                placeholder="Search broadcasts…" 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={loading}
              />
            </div>
            <select 
              className="form-select" 
              style={{ width: 'auto', padding: '6px 10px', fontSize: '12px' }}
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              disabled={loading}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: '30px' }}>
                  <input 
                    type="checkbox" 
                    checked={selectedLogs.length === broadcasts.length && broadcasts.length > 0}
                    onChange={handleSelectAll}
                    disabled={loading}
                  />
                </th>
                <th onClick={() => handleSort('title')} style={{ cursor: 'pointer' }}>
                  Title {getSortIcon('title')}
                </th>
                <th onClick={() => handleSort('type')} style={{ cursor: 'pointer' }}>
                  Type {getSortIcon('type')}
                </th>
                <th onClick={() => handleSort('message')} style={{ cursor: 'pointer' }}>
                  Message {getSortIcon('message')}
                </th>
                <th onClick={() => handleSort('recipient_count')} style={{ cursor: 'pointer' }}>
                  Recipients {getSortIcon('recipient_count')}
                </th>
                <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer' }}>
                  Created {getSortIcon('created_at')}
                </th>
                <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                  Status {getSortIcon('status')}
                </th>
                <th style={{ width: '120px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && broadcasts.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-light)' }}>
                    <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center' }}>
                      <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
                    </div>
                    <div>Loading broadcasts...</div>
                  </td>
                </tr>
              ) : broadcasts.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-light)' }}>
                    <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center' }}><Inbox size={40} strokeWidth={1.5} /></div>
                    <div>No broadcasts found</div>
                    <div style={{ fontSize: '12px', marginTop: '4px' }}>
                      Try adjusting your filters or search term
                    </div>
                  </td>
                </tr>
              ) : (
                broadcasts.map(b => (
                  <tr key={b.id}>
                    <td>
                      <input 
                        type="checkbox" 
                        checked={selectedLogs.includes(b.id)}
                        onChange={() => handleSelectLog(b.id)}
                        disabled={loading}
                      />
                    </td>
                    <td>
                      <div style={{ fontSize: '13px', fontWeight: '500' }}>{b.title || 'Untitled'}</div>
                    </td>
                    <td>
                      <span className="badge" style={{ 
                        background: `${getTypeColors(b.type)}18`, 
                        color: getTypeColors(b.type), 
                        fontSize: '11px' 
                      }}>
                        {b.type || 'Broadcast'}
                      </span>
                    </td>
                    <td style={{ 
                      maxWidth: '180px', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap', 
                      fontSize: '12px', 
                      color: 'var(--text-mid)' 
                    }}>
                      {b.message || 'No message'}
                    </td>
                    <td style={{ fontSize: '13px', fontWeight: '600' }}>
                      {b.recipient_count || 0}
                    </td>
                    <td style={{ fontSize: '12px', color: 'var(--text-light)', whiteSpace: 'nowrap' }}>
                      {formatDate(b.created_at)}
                    </td>
                    <td>{getStatusBadge(b.status)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button 
                          className="btn btn-ghost btn-sm btn-icon" 
                          onClick={() => handleViewBroadcast(b)}
                          title="View Details"
                          disabled={loading}
                        >
                          <Eye size={14} />
                        </button>
                        {(b.status === 'draft' || b.status === 'scheduled') && (
                          <button
                            className="btn btn-ghost btn-sm btn-icon"
                            onClick={() => handleEditBroadcast(b)}
                            title="Edit"
                            disabled={loading}
                          >
                            <Pencil size={14} />
                          </button>
                        )}
                        {b.status === 'scheduled' && (
                          <button
                            className="btn btn-ghost btn-sm btn-icon"
                            onClick={() => handleCancelBroadcast(b)}
                            title="Cancel"
                            style={{ color: 'var(--red)' }}
                            disabled={loading}
                          >
                            <XCircle size={14} />
                          </button>
                        )}
                        <button
                          className="btn btn-ghost btn-sm btn-icon"
                          onClick={() => handleDeleteBroadcast(b)}
                          title="Delete"
                          style={{ color: 'var(--red)' }}
                          disabled={loading}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            padding: '14px 0 0', 
            borderTop: '1px solid var(--border)', 
            marginTop: '4px',
            flexWrap: 'wrap',
            gap: '10px'
          }}>
            <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
              Showing {((currentPageNum - 1) * itemsPerPage) + 1} to {Math.min(currentPageNum * itemsPerPage, totalItems)} of {totalItems} broadcasts
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <button 
                className="btn btn-ghost btn-sm" 
                onClick={() => goToPage(currentPageNum - 1)}
                disabled={currentPageNum === 1 || loading}
                style={{ opacity: currentPageNum === 1 ? 0.5 : 1 }}
              >
                ← Prev
              </button>
              {getPageNumbers().map(page => (
                <button 
                  key={page}
                  className={`btn btn-sm ${page === currentPageNum ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => goToPage(page)}
                  disabled={loading}
                >
                  {page}
                </button>
              ))}
              <button 
                className="btn btn-ghost btn-sm" 
                onClick={() => goToPage(currentPageNum + 1)}
                disabled={currentPageNum === totalPages || loading}
                style={{ opacity: currentPageNum === totalPages ? 0.5 : 1 }}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .stat-change {
          font-size: 12px;
          font-weight: 600;
          margin-top: 2px;
        }
        .stat-change.up {
          color: var(--green);
        }
        .stat-change.down {
          color: var(--red);
        }
        .badge {
          padding: 3px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
          display: inline-block;
        }
        .badge-green {
          background: var(--green-bg);
          color: var(--green);
        }
        .badge-red {
          background: var(--red-bg);
          color: var(--red);
        }
        .badge-amber {
          background: var(--amber-bg);
          color: var(--amber);
        }
        .badge-blue {
          background: var(--blue-bg);
          color: var(--blue);
        }
        .badge-navy {
          background: var(--navy);
          color: var(--gold-light);
        }
        .btn-primary {
          background: var(--gold);
          color: var(--navy);
          border: none;
        }
        .btn-primary:hover {
          background: var(--gold-dark);
        }
        .table-wrap {
          overflow-x: auto;
        }
        table {
          width: 100%;
          border-collapse: collapse;
        }
        th {
          text-align: left;
          padding: 10px 12px;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--text-light);
          border-bottom: 2px solid var(--border);
          user-select: none;
        }
        td {
          padding: 10px 12px;
          border-bottom: 1px solid var(--border-light);
          font-size: 13px;
        }
        tr:hover td {
          background: var(--cream);
        }
        input[type="checkbox"] {
          cursor: pointer;
          width: 16px;
          height: 16px;
        }
        .stat-card.amber .stat-icon {
          color: var(--amber);
        }
        .stat-card.amber .stat-value {
          color: var(--amber);
        }
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .card-header {
            flex-direction: column;
            align-items: stretch !important;
            gap: 10px;
          }
        }
      `}</style>
    </>
  );
};

export default MessageLog;